import React, { useState, useEffect } from 'react';
import VideoKinematicsResultsPanel, { VideoKinematicsData } from '../components/VideoKinematicsResultsPanel';
import './VideoKinematicsPage.css';
import { buildApiUrl } from '../config/api';

/**
 * Video Kinematics Analysis Page
 * 
 * This page displays video kinematics analysis results.
 * Can load from API (if analysisId provided) or display passed data.
 */

interface VideoKinematicsPageProps {
  onBack?: () => void;
  // Optional: If data is passed as prop (e.g., from API)
  initialData?: VideoKinematicsData;
  analysisId?: number;
  playerId?: number;
}

const VideoKinematicsPage: React.FC<VideoKinematicsPageProps> = ({ 
  onBack,
  initialData,
  analysisId,
  playerId
}) => {
  const [data, setData] = useState<VideoKinematicsData | null>(initialData || null);
  const [loading, setLoading] = useState(!initialData && !!analysisId);
  const [error, setError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [, setSaving] = useState(false);
  const [, setDeleting] = useState(false);

  useEffect(() => {
    const processInitialData = async () => {
      if (initialData) {
        // Normalize knee flexion metrics (backward compatibility)
        let processedData = { ...initialData };
        if (processedData.metrics) {
          const { normalizeKneeFlexionMetrics } = await import('../utils/kinematicsNormalization');
          processedData = {
            ...processedData,
            metrics: normalizeKneeFlexionMetrics(processedData.metrics)
          };
        }
        
        // Ensure overallScore and movementType are present via fallback
        if (processedData.metrics && (processedData.overallScore === undefined && processedData.overall_score === undefined)) {
          try {
            const { findBenchmark } = await import('../config/kinematicsBenchmarks');
            const { computeScoring } = await import('../utils/kinematicsScoring');
            
            const movementName = processedData.movement_name || processedData.movementType || 'Instep Shot – Power';
            const ageBand = processedData.ageBand || 'U15–U17';
            const level = processedData.level || 'Standard';
            
            const benchmark = findBenchmark(movementName, ageBand, level);
            if (benchmark) {
              const scoringResult = computeScoring(processedData.metrics, benchmark.params);
              processedData.overallScore = scoringResult.overallScore;
              processedData.overall_score = scoringResult.overallScore;
            } else {
              console.warn("No benchmark found for client-side scoring fallback for initial data.");
              processedData.overallScore = 0;
              processedData.overall_score = 0;
            }
          } catch (err) {
            console.error('[VideoKinematicsPage] Failed to compute fallback overallScore for initial data:', err);
            processedData.overallScore = 0;
            processedData.overall_score = 0;
          }
        }
        if (!processedData.movementType && processedData.movement_name) {
          processedData.movementType = processedData.movement_name;
        }
        
        // Ensure debugVideoUrl is included from multiple possible locations
        if (!processedData.debugVideoUrl && !processedData.video?.debugUrl) {
          processedData.debugVideoUrl = processedData.debug_video_url || processedData.debugVideo || null;
        }
        
        setData(processedData);
        setIsSaved(!!analysisId);
      } else if (analysisId) {
        loadAnalysis();
      }
    };
    
    processInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId, initialData]);

  const loadAnalysis = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildApiUrl(`/video-kinematics/${analysisId}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        let analysis = result.analysis;
        
        // Normalize knee flexion metrics (backward compatibility)
        if (analysis.metrics) {
          const { normalizeKneeFlexionMetrics } = await import('../utils/kinematicsNormalization');
          analysis = {
            ...analysis,
            metrics: normalizeKneeFlexionMetrics(analysis.metrics)
          };
        }
        
        // Extract or compute overallScore
        let overallScore: number | undefined = analysis.overallScore || analysis.overall_score;
        
        // If overallScore is missing, compute it client-side as fallback
        if (overallScore === undefined && analysis.metrics) {
          try {
            const { findBenchmark } = await import('../config/kinematicsBenchmarks');
            const { computeScoring } = await import('../utils/kinematicsScoring');
            
            const movementName = analysis.movementType || analysis.movement_name;
            const ageBand = analysis.ageBand || 'U15–U17';
            const level = analysis.level || 'Standard';
            
            const benchmark = findBenchmark(movementName, ageBand, level);
            if (benchmark) {
              const scoringResult = computeScoring(analysis.metrics, benchmark.params);
              overallScore = scoringResult.overallScore;
              console.log(`[VideoKinematicsPage] Computed fallback overallScore: ${overallScore}`);
            } else {
              console.warn(`[VideoKinematicsPage] No benchmark found, using 0`);
              overallScore = 0;
            }
          } catch (err) {
            console.error('[VideoKinematicsPage] Failed to compute fallback overallScore:', err);
            overallScore = 0;
          }
        }
        
        setData({
          movement_name: analysis.movement_name,
          ageBand: analysis.ageBand,
          level: analysis.level,
          kickingFoot: analysis.kickingFoot,
          timestamp: analysis.timestamp,
          video: analysis.video || {},
          keyFrames: analysis.keyFrames || {},
          metrics: analysis.metrics || {},
          metricsRangeStatus: analysis.metricsRangeStatus || undefined, // API-provided rangeStatus
          overallScore: overallScore || 0,
          overall_score: overallScore || 0,
          // Include debug video URL from multiple possible locations
          debugVideoUrl: analysis.debugVideoUrl || analysis.video?.debugUrl || analysis.debug_video_url || analysis.debugVideo || null
        });
        setIsSaved(analysis.saved || false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to load analysis');
      }
    } catch (err) {
      setError('Failed to load analysis. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!data || !playerId || analysisId) return; // Can't save if already saved or no data

    const token = localStorage.getItem('access_token');
    if (!token) {
      setError('Authentication required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Compute overall score from metrics
      const { findBenchmark } = await import('../config/kinematicsBenchmarks');
      const { computeScoring } = await import('../utils/kinematicsScoring');
      const benchmark = findBenchmark(data.movement_name, data.ageBand, data.level);
      const scoringResult = benchmark ? computeScoring(data.metrics, benchmark.params) : null;
      const overallScore = scoringResult?.overallScore || 0;

      // First create the analysis
      const createResponse = await fetch(buildApiUrl('/video-kinematics/create'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          player_id: playerId,
          movement_name: data.movement_name,
          ageBand: data.ageBand,
          level: data.level,
          kickingFoot: data.kickingFoot,
          overall_score: overallScore,
          metrics: data.metrics,
          keyFrames: data.keyFrames,
          video: data.video,
          saved: true
        })
      });

      if (createResponse.ok) {
        const result = await createResponse.json();
        // Then mark as saved
        const saveResponse = await fetch(buildApiUrl(`/video-kinematics/${result.analysis_id}/save`), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (saveResponse.ok) {
          setIsSaved(true);
          alert('Video analysis saved!');
        } else {
          throw new Error('Failed to save analysis');
        }
      } else {
        const errorData = await createResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create analysis');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save analysis');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!analysisId) {
      // If not saved, just go back
      if (onBack) onBack();
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      setError('Authentication required');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this analysis?')) {
      return;
    }

    try {
      setDeleting(true);
      setError(null);

      const response = await fetch(buildApiUrl(`/video-kinematics/${analysisId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Video analysis deleted');
        if (onBack) onBack();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete analysis');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete analysis');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="video-kinematics-page">
        <div className="loading">Loading analysis...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="video-kinematics-page">
        <div className="error">{error}</div>
        {onBack && (
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        )}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="video-kinematics-page">
        <div className="error">No analysis data available</div>
        {onBack && (
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="video-kinematics-page">
      <div className="page-header">
        {onBack && (
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        )}
        <h1>Video Kinematics Analysis</h1>
        {error && (
          <div className="error-message">{error}</div>
        )}
      </div>

      <div className="page-content">
        <VideoKinematicsResultsPanel 
          data={data} 
          analysisId={analysisId}
          playerId={playerId}
          isSaved={isSaved}
          onSave={handleSave}
          onDelete={handleDelete}
          onBack={onBack}
        />
      </div>
    </div>
  );
};

export default VideoKinematicsPage;

